import {Color} from '@benchling/nucleus-ui';
import {StyleSheet, css} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';
import _ from 'underscore';

import {BORDER_RADIUS_DEFAULT} from 'styles/StyleConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {ElementMouseEvent} from 'util/Types';

export type FormInputType = string | 'text' | 'number' | 'textarea' | 'checkbox';

/**
 * Indicate where in an input group the FormInput appears to control rounding:
 *
 * 'left': results in square corners on the right side of the input.
 *
 *          ╭-----------+---╮
 *          | FormInput | X |
 *          ╰-----------+---╯
 *
 * 'right': results in square corners on the left side of the input.
 *
 *      ╭---+-----------╮
 *      | X | FormInput |
 *      ╰---+-----------╯
 *
 * 'middle': results in square corners on both sides of the input.
 *
 *      ╭---+-----------+---╮
 *      | X | FormInput | X |
 *      ╰---+-----------+---╯
 *
 * undefined (default): results in rounded corners on both sides of the input.
 *
 *          ╭-----------╮
 *          | FormInput |
 *          ╰-----------╯
 */
export type InputGroupPosition = 'left' | 'middle' | 'right';

interface FormInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  className?: string;
  error?: string;
  type: FormInputType;
  focusOnMount: boolean;
  id?: string;
  name?: string;
  onClick?: (e: ElementMouseEvent) => void;
  placeholder?: string;
  selectAllOnClick: boolean;
  dataTestAttrs?: DataTestAttrs;
  rows?: number;
  maxLength?: number;
  inputGroupPosition?: InputGroupPosition;
  disabled?: boolean;
}

class FormInput extends React.Component<FormInputProps> {
  static defaultProps = {
    type: 'text',
    focusOnMount: false,
    selectAllOnClick: false,
  };

  inputRef = React.createRef<HTMLInputElement | HTMLTextAreaElement>();

  override componentDidMount(): void {
    if (this.props.focusOnMount) {
      this.inputRef.current?.focus();
    }
  }

  // Deprecated: use the focusOnMount property instead
  focus(): void {
    this.inputRef.current!.select();
  }

  _handleClick(e: ElementMouseEvent): void {
    if (this.props.selectAllOnClick) {
      this.inputRef.current!.select();
    }
    if (this.props.onClick) {
      this.props.onClick(e);
    }
  }

  getInputGroupStyle(): object | null {
    switch (this.props.inputGroupPosition) {
      case 'left':
        return styles.inputGroupLeft;
      case 'right':
        return styles.inputGroupRight;
      case 'middle':
        return styles.inputGroupMiddle;
      default:
        return null;
    }
  }

  override render(): JSX.Element {
    const sharedProps = {
      // eslint-disable-next-line no-restricted-syntax
      ..._.omit(
        this.props,
        'error',
        'focusOnMount',
        'selectAllOnClick',
        'dataTestAttrs',
        'rows',
        'type',
        'inputGroupPosition'
      ),
      ref: this.inputRef,
      className: classNames(
        this.props.className,
        'form-control',
        css(styles.input, this.getInputGroupStyle()),
        this.props.disabled && css(styles.disabled),
        {
          'fieldInput-input--error': Boolean(this.props.error),
        }
      ),
      onClick: (e: ElementMouseEvent) => {
        this._handleClick(e);
      },
      ...getTestProps(this.props.dataTestAttrs),
    };

    return this.props.type === 'textarea' ? (
      <textarea {...sharedProps} rows={this.props.rows} aria-invalid={Boolean(this.props.error)} />
    ) : (
      <input {...sharedProps} type={this.props.type} aria-invalid={Boolean(this.props.error)} />
    );
  }
}

export default FormInput;

const styles = StyleSheet.create({
  disabled: {
    backgroundColor: Color.GRAY_100,
    borderColor: Color.GRAY_100,
    // Set on color and placeholder color so the text is the right color for placeholders and values
    color: Color.GRAY_600,
    '::placeholder': {
      color: Color.GRAY_600,
    },
  },

  input: {
    borderRadius: BORDER_RADIUS_DEFAULT,
  },
  inputGroupLeft: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
  inputGroupRight: {
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
  },
  inputGroupMiddle: {
    borderRadius: 0,
  },
});
